import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { MBTI_GROUPS } from "@/lib/mbti";
import PhotoUpload, { Avatar } from "@/components/PhotoUpload";
import { ShieldCheck, Save, UserPlus, Trash2 } from "lucide-react";

const CLUBS = MBTI_GROUPS.map((g) => g.club);
const MBTI_CODES = MBTI_GROUPS.flatMap((g) => (g.types || []).map((t) => t.code));

/**
 * Dedicated admin page for managing Coaches (filtered view of /users).
 * Each coach must be assigned a club; MBTI is optional but recommended.
 * Lives at /coaches (admin only).
 */
export default function CoachesAdmin() {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState({});

  const [lead, setLead] = useState(null);
  const refresh = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { api.get("/club-leadership").then((r) => setLead(r.data)).catch(() => setLead({})); }, []);
  useEffect(() => { refresh(); }, []);

  const coaches = useMemo(() => users.filter((u) => u.role === "coach"), [users]);
  const grouped = useMemo(() => {
    const out = { _unassigned: [] };
    CLUBS.forEach((c) => { out[c] = []; });
    coaches.forEach((c) => {
      if (c.club && out[c.club]) out[c.club].push(c);
      else out._unassigned.push(c);
    });
    return out;
  }, [coaches]);

  const updateCoach = async (id, patch) => {
    setPending((p) => ({ ...p, [id]: true }));
    try {
      await api.put(`/users/${id}`, patch);
      toast.success("Secretário atualizado");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setPending((p) => ({ ...p, [id]: false }));
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remover a conta de secretário ${name}?\nO utilizador será apagado e perderá acesso à plataforma.`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Conta removida");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resetPassword = async (id, name) => {
    const newPwd = window.prompt(`Nova password para ${name} (mín. 6 caracteres):`);
    if (!newPwd) return;
    if (newPwd.length < 6) { toast.error("Password deve ter pelo menos 6 caracteres."); return; }
    try { await api.put(`/users/${id}`, { password: newPwd }); toast.success("Password atualizada."); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="coaches-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="kw-overline">Administração</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter">Secretários</h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
            O Secretário e o Presidente de cada clube são escolhidos no Plantel. O Secretário não pode
            ser do perfil do clube que acompanha; o Presidente é do perfil do próprio clube.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
        <a href="/roster" className="kw-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="coaches-goto-roster">
          <ShieldCheck className="w-4 h-4" /> Mudar no Plantel
        </a>
        <a href="/users" className="kw-btn-secondary inline-flex items-center gap-2 text-xs" data-testid="coaches-goto-users">
          <UserPlus className="w-4 h-4" /> Criar/editar utilizadores
        </a>
        </div>
      </div>

      {/* Unassigned highlight */}
      {grouped._unassigned.length > 0 && (
        <div className="kw-card p-5 border-[#E50000]/50 bg-[#E50000]/5" data-testid="coaches-unassigned">
          <div className="kw-overline text-[#E50000] mb-3">⚠ Sem clube atribuído ({grouped._unassigned.length})</div>
          <div className="space-y-2">
            {grouped._unassigned.map((c) => (
              <CoachRow key={c.id} coach={c} pending={pending[c.id]}
                onChange={(patch) => updateCoach(c.id, patch)}
                onPhotoChange={refresh}
                onResetPassword={() => resetPassword(c.id, c.name)}
                onRemove={() => remove(c.id, c.name)} />
            ))}
          </div>
        </div>
      )}

      {/* By club */}
      <div className="grid lg:grid-cols-2 gap-6">
        {CLUBS.map((club) => (
          <div key={club} className="kw-card p-5" data-testid={`coaches-club-${club}`}>
            <div className="flex items-center gap-2 mb-4 border-b border-[#222] pb-3">
              <ShieldCheck className="w-5 h-5 text-[#E50000]" />
              <div>
                <div className="kw-overline">Clube</div>
                <div className="font-display text-2xl font-black uppercase">{club}</div>
              </div>
              <div className="ml-auto text-[10px] uppercase tracking-widest text-[#737373]" data-testid={`coaches-count-${club}`}>
                {(lead?.[club]?.secretaries.length || 0) + grouped[club].length} secretário(s)
              </div>
            </div>
            <LeaderList title="Secretário" people={lead?.[club]?.secretaries} testid={`lead-secretaries-${club}`} />
            <LeaderList title="Presidente do Clube" people={lead?.[club]?.presidents} testid={`lead-presidents-${club}`} />
            {grouped[club].length > 0 && (
              <div className="kw-overline text-[10px] mt-4 mb-2">Contas de secretário</div>
            )}
            {grouped[club].length === 0 ? null : (
              <div className="space-y-2">
                {grouped[club].map((c) => (
                  <CoachRow key={c.id} coach={c} pending={pending[c.id]}
                    onChange={(patch) => updateCoach(c.id, patch)}
                    onPhotoChange={refresh}
                    onResetPassword={() => resetPassword(c.id, c.name)}
                    onRemove={() => remove(c.id, c.name)} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LeaderList({ title, people, testid }) {
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="kw-overline text-[10px] mb-1.5">{title}</div>
      {!people ? <div className="text-xs text-[#555]">A carregar…</div>
        : people.length === 0 ? (
          <p className="text-xs text-[#737373] italic">Ainda não escolhido no Plantel.</p>
        ) : people.map((p) => (
          <div key={p.id} className="border border-[#222] p-2.5 flex items-center gap-3 mb-1.5">
            <Avatar photoUrl={p.photo_url} name={p.name} size={36} />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm truncate">{p.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] truncate">
                {p.mbti || "sem MBTI"} · {p.email}
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}

function CoachRow({ coach, pending, onChange, onPhotoChange, onResetPassword, onRemove }) {
  return (
    <div className="border border-[#222] p-3 flex flex-wrap items-center gap-3" data-testid={`coach-row-${coach.id}`}>
      <PhotoUpload userId={coach.id} photoUrl={coach.photo_url} name={coach.name} size="sm" onChange={onPhotoChange} />
      <div className="flex-1 min-w-[180px]">
        <div className="font-display font-black uppercase tracking-tight">{coach.name}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">{coach.email}</div>
      </div>
      <input
        defaultValue={coach.phone || ""}
        onBlur={(e) => {
          const val = e.target.value.trim();
          if (val !== (coach.phone || "")) onChange({ phone: val || null });
        }}
        placeholder="+351 …"
        className="kw-input !py-1.5 !w-36 text-xs"
        disabled={pending}
        data-testid={`coach-phone-${coach.id}`}
      />
      <select className="kw-input !py-1.5 !w-36 text-xs" value={coach.club || ""}
        onChange={(e) => onChange({ club: e.target.value || null })}
        disabled={pending} data-testid={`coach-club-${coach.id}`}>
        <option value="">— sem clube —</option>
        {CLUBS.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select className="kw-input !py-1.5 !w-32 text-xs" value={coach.coach_mbti || ""}
        onChange={(e) => onChange({ coach_mbti: e.target.value || null })}
        disabled={pending} data-testid={`coach-mbti-${coach.id}`}>
        <option value="">— MBTI —</option>
        {MBTI_CODES.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      {coach.coach_mbti && coach.coach_personality_label && (
        <span className="text-[10px] uppercase tracking-widest text-[#3B82F6]" data-testid={`coach-label-${coach.id}`}>{coach.coach_personality_label}</span>
      )}
      <button onClick={onResetPassword} disabled={pending}
        className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white"
        data-testid={`coach-reset-pwd-${coach.id}`}>
        Reset password
      </button>
      <button onClick={onRemove} disabled={pending}
        className="text-[10px] uppercase tracking-widest font-bold text-[#737373] hover:text-[#E50000] flex items-center gap-1"
        data-testid={`coach-remove-${coach.id}`}>
        <Trash2 className="w-3 h-3" /> Remover
      </button>
    </div>
  );
}
